import React, { useEffect, useState } from 'react';
import StylistIntro from './StylistIntro';
import styled from '@emotion/native';
import { IsDesktopProps } from '@/shared/styles/types';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import StylistFindClient from './StylistFindClient';
import StylistMatchFound from './StylistMatchFound';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { useShallow } from 'zustand/react/shallow';
import { useLocalSearchParams } from 'expo-router';
import useStore from '@/stores/useStore';

const INTRO_STEP = 0;
const FIND_CLIENT_STEP = 1;
const MATCH_FOUND_STEP = 2;

interface Props {
  handleStartSession: (guestId: number) => void;
}

const StylistInstantStyling = ({ handleStartSession }: Props) => {
  const isDesktop = useIsDesktop();
  const { id: requestId } = useLocalSearchParams();

  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const {
    matchStatus,
    setMatchStatus,
    isInstantStylingActive,
    resetMatchStatus,
  } = useInstantStylingStore(
    useShallow((state) => ({
      matchStatus: state.matchStatus,
      setMatchStatus: state.setMatchStatus,
      isInstantStylingActive: state.isInstantStylingActive,
      resetMatchStatus: state.resetMatchStatus,
    })),
  );

  const [step, setStep] = useState<number>(INTRO_STEP);

  useEffect(() => {
    if (requestId) {
      setMatchStatus({
        requestId: Number(requestId),
      });
      return;
    }

    if (matchStatus?.stylistAccepted) {
      return;
    }

    if (isInstantStylingActive) {
      setStep(FIND_CLIENT_STEP);
    } else {
      setStep(INTRO_STEP);
    }
  }, [
    isInstantStylingActive,
    matchStatus?.stylistAccepted,
    requestId,
    setMatchStatus,
  ]);

  useEffect(() => {
    if (!matchStatus) return;

    if (matchStatus.requestId && !matchStatus.stylistAccepted) {
      setStep(MATCH_FOUND_STEP);
      return;
    }
  }, [matchStatus, requestId, setMatchStatus]);

  useEffect(() => {
    if (matchStatus?.clientCancelled) {
      resetMatchStatus();
      setSnackbarState({
        open: true,
        message: 'The request was canceled by the client',
        severity: 'info',
      });
      setStep(FIND_CLIENT_STEP);
    }
  }, [matchStatus, resetMatchStatus, setSnackbarState]);

  useEffect(() => {
    if (matchStatus?.clientDeclined) {
      resetMatchStatus();
      setSnackbarState({
        open: true,
        message: 'The match was declined by the client',
        severity: 'info',
      });
      setStep(FIND_CLIENT_STEP);
    }
  }, [matchStatus, resetMatchStatus, setSnackbarState]);

  const onStylistDeclineMatch = () => {
    setStep(FIND_CLIENT_STEP);
  };

  const renderStep = () => {
    switch (step) {
      case INTRO_STEP:
        return <StylistIntro setStep={setStep} />;
      case FIND_CLIENT_STEP:
        return <StylistFindClient setStep={setStep} />;
      case MATCH_FOUND_STEP:
        return (
          <StylistMatchFound
            handleStartSession={handleStartSession}
            onStylistDeclineMatch={onStylistDeclineMatch}
          />
        );
      default:
        return null;
    }
  };

  return <Container isDesktop={isDesktop}>{renderStep()}</Container>;
};

export default StylistInstantStyling;

const Container = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: isDesktop ? 48 : 0,
  paddingBottom: isDesktop ? 36 : 0,
}));
